import { useState } from 'react';
import { AsideDialog } from '@mews-commander/ui';
import { LocalizedText, createZodFormResolver, showSuccessAlert, lt, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Button, DialogProps, SelectionCardGroup, Stack } from '@optimus-web/core';
import { useAssignProperties } from '../queries';
import { PropertiesSelectField } from './PropertiesSelectField';
import {
    ENTERPRISE_GROUPS_IDS_FIELD_NAME,
    EnterpriseFormValues,
    EnterpriseGroupsFormValues,
    enterpriseGroupsValidationSchema,
    enterpriseValidationSchema,
} from './schema';
import { PropertyGroupsSelectField } from './PropertyGroupsSelectField';

interface AssignPropertiesDialogProps {
    rateGroupId: string;
    isOpened: boolean;
    onSuccess: () => void;
    onClose: () => void;
}

enum ActiveTab {
    properties = 'properties',
    propertyGroups = 'propertyGroups',
}

export const AssignPropertiesDialog = ({ isOpened, onClose, onSuccess, rateGroupId }: AssignPropertiesDialogProps) => {
    const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.propertyGroups);

    const { mutate, isPending } = useAssignProperties();

    const assignProperties = (formValues: EnterpriseFormValues) => {
        mutate(
            { ...formValues, centralRateGroupId: rateGroupId },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        title: lt('AssignPropertiesToCentralRateGroupSuccessTitle', {
                            count: formValues.enterpriseIds.length,
                        }),
                        description: lt('AssignPropertiesToCentralRateGroupSuccessDescription'),
                    });
                    onSuccess();
                },
            }
        );
    };

    const assignPropertyGroups = (formValues: EnterpriseGroupsFormValues) => {
        mutate(
            { enterpriseGroupIds: formValues[ENTERPRISE_GROUPS_IDS_FIELD_NAME], centralRateGroupId: rateGroupId },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        title: lt('AssignPropertyGroupsToCentralRateGroupSuccessTitle', {
                            count: formValues[ENTERPRISE_GROUPS_IDS_FIELD_NAME].length,
                        }),
                        description: lt('AssignPropertyGroupsToCentralRateGroupSuccessDescription'),
                    });
                    onSuccess();
                },
            }
        );
    };

    const enterpriseResolver = createZodFormResolver(enterpriseValidationSchema);
    const enterpriseGroupsResolver = createZodFormResolver(enterpriseGroupsValidationSchema);

    const {
        renderFormContent: renderEnterpriseForm,
        submit: submitProperties,
        reset: resetEnterpriseForm,
        hasDirtyFields: hasDirtyFieldsEnterpriseForm,
    } = useFormControls<EnterpriseFormValues>({
        onSubmit: assignProperties,
        resolver: enterpriseResolver,
        mode: 'onSubmit',
    });

    const {
        renderFormContent: renderEnterpriseGroupsForm,
        submit: submitPropertiesGroups,
        reset: resetEnterpriseGroupsForm,
        hasDirtyFields: hasDirtyFieldsEnterpriseGroupsForm,
    } = useFormControls<EnterpriseGroupsFormValues>({
        onSubmit: assignPropertyGroups,
        resolver: enterpriseGroupsResolver,
        mode: 'onSubmit',
    });

    const handleTabChange = (tab: ActiveTab) => {
        setActiveTab(tab);
        if (tab === ActiveTab.properties) {
            resetEnterpriseForm();

            return;
        }

        resetEnterpriseGroupsForm();
    };

    const actions: DialogProps['actions'] = [
        {
            children: <LocalizedText textKey={TextKey.Assign} />,
            type: 'submit',
            disabled:
                activeTab === ActiveTab.properties
                    ? !hasDirtyFieldsEnterpriseForm
                    : !hasDirtyFieldsEnterpriseGroupsForm,
            onClick: activeTab === ActiveTab.properties ? submitProperties : submitPropertiesGroups,
            variant: 'primary',
        },
    ];

    return (
        <AsideDialog
            actions={actions}
            active={isOpened}
            onClose={onClose}
            loading={isPending}
            title={<LocalizedText textKey={TextKey.AssignPropertiesToCentralRateGroup} />}
            footerDetail={
                <Button variant="tertiary" onClick={onClose} type="submit">
                    <LocalizedText textKey={TextKey.Cancel} />
                </Button>
            }
        >
            <Stack spacing="300" vertical>
                <SelectionCardGroup
                    templateColumns="repeat(2, 1fr)"
                    type="radio"
                    onChange={(_, tab) => handleTabChange(tab as ActiveTab)}
                    inputValue={activeTab}
                    options={[
                        {
                            label: lt('PropertyGroupPlural'),
                            name: ActiveTab.propertyGroups,
                            value: ActiveTab.propertyGroups,
                        },
                        {
                            label: lt('Properties'),
                            name: ActiveTab.properties,
                            value: ActiveTab.properties,
                        },
                    ]}
                />

                <Banner
                    primaryText={
                        activeTab === ActiveTab.properties ? (
                            <LocalizedText textKey={TextKey.AssignPropertiesToCentralRateGroupBanner} />
                        ) : (
                            <LocalizedText textKey={TextKey.AssignPropertyGroupsToCentralRateGroupBanner} />
                        )
                    }
                    variant="info"
                />
                {activeTab === ActiveTab.properties
                    ? renderEnterpriseForm(<PropertiesSelectField rateGroupId={rateGroupId} />)
                    : renderEnterpriseGroupsForm(<PropertyGroupsSelectField rateGroupId={rateGroupId} />)}
            </Stack>
        </AsideDialog>
    );
};
